import { Link } from "react-router-dom";
import { ArrowRight, Building2, Home, MapPin, Wrench } from "lucide-react";
import { InfoSection } from "../info/InfoSections";
import { TextBody } from "../info/TextBody";
import { COMPANY } from "../../lib/constants";

const slugId = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export const CityIntro = ({ page }) => (
  <InfoSection id="over" eyebrow={`Over ${page.name}`} title={`Nieuwe kozijnen in ${page.name}`}>
    <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
      <TextBody text={page.intro} className="lg:col-span-7" testid="city-intro" />
      <aside className="card h-fit p-6 lg:col-span-5" data-testid="city-location-card">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-maas-soft text-maas"><MapPin className="h-5 w-5" aria-hidden="true" /></span>
        <h3 className="mt-4 text-[17px] font-semibold text-ink">Vestiging en werkgebied</h3>
        <p className="mt-2 text-[14.5px] leading-relaxed text-slate-600">
          Maas Kozijn is gevestigd in {COMPANY.locality} en werkt in Zuid-Holland. {page.name === COMPANY.locality ? "Hier zijn we het snelst ter plaatse." : `In ${page.name} komen we bij u thuis voor advies, inmeten en montage.`}
        </p>
        {page.region && <p className="mt-3 text-[13px] font-semibold uppercase tracking-[0.12em] text-slate-500">{page.region}</p>}
        {page.nearby?.length > 0 && (
          <>
            <p className="mt-5 text-[14px] font-semibold text-ink">Ook in de omgeving</p>
            <ul className="mt-2 flex flex-wrap gap-2" data-testid="city-nearby">
              {page.nearby.map((n) => <li key={n} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[13px] text-slate-700">{n}</li>)}
            </ul>
          </>
        )}
      </aside>
    </div>
  </InfoSection>
);

export const CityWijken = ({ page }) => (
  <InfoSection id="wijken" tone="slate" eyebrow="Wijken" title={`Wijken en kernen in ${page.name}`}
    lead="Per wijk verschillen bouwperiode, woningtype en aandachtspunten. Zo pakken we dat aan.">
    <ul className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="city-wijken">
      {page.wijken.map((w) => (
        <li key={w.name} className="card p-6" data-testid={`city-wijk-${slugId(w.name)}`}>
          <h3 className="flex items-center gap-2 text-[17px] font-semibold text-ink"><MapPin className="h-4 w-4 shrink-0 text-gold" aria-hidden="true" />{w.name}</h3>
          {w.text && <TextBody text={w.text} className="mt-2 text-[14.5px]" />}
        </li>
      ))}
    </ul>
  </InfoSection>
);

export const CityWoningen = ({ page }) => (
  <InfoSection id="woningen" eyebrow="Woningtypes" title={`Veelvoorkomende woningen in ${page.name}`}>
    <ul className="grid gap-4 sm:grid-cols-2" data-testid="city-woningtypes">
      {page.woningtypes.map((w) => (
        <li key={w.title} className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-5">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-maas-soft text-maas"><Home className="h-5 w-5" aria-hidden="true" /></span>
          <div><h3 className="text-[16px] font-semibold text-ink">{w.title}</h3>{w.text && <p className="mt-1.5 text-[14.5px] leading-relaxed text-slate-600">{w.text}</p>}</div>
        </li>
      ))}
    </ul>
  </InfoSection>
);

export const CityLokaal = ({ page }) => (
  <InfoSection id="aandachtspunten" tone="slate" eyebrow="Lokale aandachtspunten" title={`Waar wij in ${page.name} op letten`}>
    <div className="grid gap-x-12 gap-y-10 lg:grid-cols-2" data-testid="city-lokaal">
      {page.lokaal.map((b) => (
        <article key={b.kop} data-testid="city-lokaal-block">
          <h3 className="text-[20px] font-bold leading-snug text-ink">{b.kop}</h3>
          <TextBody text={b.tekst} className="mt-3" />
        </article>
      ))}
    </div>
  </InfoSection>
);

const PRODUCTS = [
  { href: "/kunststof-kozijnen", title: "Kunststof kozijnen", text: "Onderhoudsarm, goed isolerend en in veel kleuren en houtnerf." },
  { href: "/aluminium-kozijnen", title: "Aluminium kozijnen", text: "Slanke profielen en grote glasvlakken voor een strakke gevel." },
  { href: "/kunststof-schuifpui", title: "Kunststof schuifpui", text: "Meer licht en een open verbinding met de tuin, prijsbewust." },
  { href: "/aluminium-hefschuifpui", title: "Aluminium (hef)schuifpui", text: "Licht bedienbaar, ook bij brede openingen." },
  { href: "/deuren", title: "Voor-, achter- en tuindeuren", text: "Kunststof en aluminium deuren met meerpuntsluiting." },
  { href: "/montage", title: "Montage en afwerking", text: "Demontage, plaatsing, afdichting en nette afwerking." },
];

export const ProductGrid = ({ location }) => (
  <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid={`${location}-products`}>
    {PRODUCTS.map((p, i) => (
      <li key={p.href}>
        <Link to={p.href} className="card card-hover group flex h-full gap-4 p-5" data-testid={`${location}-product-${p.href.slice(1)}`}>
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-maas-soft text-maas">{i === 5 ? <Wrench className="h-5 w-5" aria-hidden="true" /> : <Building2 className="h-5 w-5" aria-hidden="true" />}</span>
          <span>
            <span className="block text-[16px] font-semibold text-ink group-hover:text-maas">{p.title}</span>
            <span className="mt-1 block text-[14px] leading-relaxed text-slate-600">{p.text}</span>
            <span className="mt-2 inline-flex items-center gap-1 text-[13.5px] font-semibold text-maas">Bekijk <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" /></span>
          </span>
        </Link>
      </li>
    ))}
  </ul>
);

export const CityProducts = ({ page }) => (
  <InfoSection id="producten" eyebrow="Aanbod" title={`Kozijnen, schuifpuien en deuren in ${page.name}`}>
    {page.diensten && <TextBody text={page.diensten} className="-mt-4 mb-8" testid="city-diensten" />}
    <ProductGrid location="city" />
  </InfoSection>
);
